import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { PageHeader } from 'components/PageHeader';
import { useEsc } from 'hooks/useEsc';
import { formatDate, isoDateStringToDate } from 'lib/riskbase';
import { theme } from 'lib/theme';
import React from 'react';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL_BACK } from 'store/actions';
import {
  EngageCommunicationUpdateSource,
  EngageCommunicationUpdateType,
  type EngageCommunicationUpdate,
} from 'types/DTOs/BlockManagerDTOs';
import { UI_DATE_FORMAT_LONG } from 'lib/constants';

const UPDATE_TYPE_LABELS: Record<EngageCommunicationUpdateType, string> = {
  // Letter Updates
  [EngageCommunicationUpdateType.LetterPrinting]: 'Printing',
  [EngageCommunicationUpdateType.LetterDispatched]: 'Dispatched',
  [EngageCommunicationUpdateType.LetterCancelled]: 'Cancelled',
  [EngageCommunicationUpdateType.LetterLocalDelivery]: 'Out for Delivery',
  [EngageCommunicationUpdateType.LetterDelivered]: 'Delivered',
  [EngageCommunicationUpdateType.LetterReturned]: 'Returned',
  // Email Updates
  [EngageCommunicationUpdateType.EmailSent]: 'Sent',
  [EngageCommunicationUpdateType.EmailDelivered]: 'Delivered',
  [EngageCommunicationUpdateType.EmailRejected]: 'Rejected',
  [EngageCommunicationUpdateType.EmailBounced]: 'Bounced',
  [EngageCommunicationUpdateType.EmailComplained]: 'Complaint',
  [EngageCommunicationUpdateType.EmailOpened]: 'Opened',
  [EngageCommunicationUpdateType.EmailClicked]: 'Clicked',
  [EngageCommunicationUpdateType.EmailDelayed]: 'Delivery Delayed',
  // BM + User Update
  [EngageCommunicationUpdateType.UniqueLinkOpened]: 'Opened Link',
  // User Updates
  [EngageCommunicationUpdateType.ContactSurveyResponded]: 'Responded',
  [EngageCommunicationUpdateType.ScheduledVisitResponded]: 'Responded',
  [EngageCommunicationUpdateType.EvidenceUploaded]: 'Uploaded Evidence',
};

const SUCCESSFUL_EMAIL_UPDATES = [
  EngageCommunicationUpdateType.EmailSent,
  EngageCommunicationUpdateType.EmailDelivered,
];

const USER_RESPONSE_UPDATES = [
  EngageCommunicationUpdateType.ContactSurveyResponded,
  EngageCommunicationUpdateType.ScheduledVisitResponded,
  EngageCommunicationUpdateType.EvidenceUploaded,
];

const UPDATE_SOURCE_LABELS: Record<EngageCommunicationUpdateSource, string> = {
  [EngageCommunicationUpdateSource.ses]: 'Email',
  [EngageCommunicationUpdateSource.stannp]: 'Letter',
  [EngageCommunicationUpdateSource.app]: 'Block Manager',
  [EngageCommunicationUpdateSource.engage]: 'Flat Contact',
};

// This will need updating when we implement the stannp updates properly
const getUpdateIcon = (
  update: EngageCommunicationUpdate,
): { updateIcon: ICONS; updateIconColor: string } => {
  if (update.updateType) {
    if (SUCCESSFUL_EMAIL_UPDATES.includes(update.updateType)) {
      return {
        updateIcon: ICONS.EnvelopeSentLight,
        updateIconColor: theme.colors.green,
      };
    }
    if (update.updateType === EngageCommunicationUpdateType.EmailOpened) {
      return {
        updateIcon: ICONS.EnvelopeOpenLight,
        updateIconColor: theme.colors.green,
      };
    }
    if (update.updateType === EngageCommunicationUpdateType.EmailClicked) {
      return {
        updateIcon: ICONS.ArrowPointerLight,
        updateIconColor: theme.colors.green,
      };
    }
    if (update.updateType === EngageCommunicationUpdateType.EmailDelayed) {
      return {
        updateIcon: ICONS.EnvelopeSentLight,
        updateIconColor: theme.colors.orange,
      };
    }
    if (
      update.updateType === EngageCommunicationUpdateType.EmailRejected ||
      update.updateType === EngageCommunicationUpdateType.EmailBounced ||
      update.updateType === EngageCommunicationUpdateType.EmailComplained
    ) {
      return {
        updateIcon: ICONS.EnvelopeFailedLight,
        updateIconColor: theme.colors.red,
      };
    }

    if (
      update.updateType === EngageCommunicationUpdateType.LetterLocalDelivery
    ) {
      return {
        updateIcon: ICONS.VanRightLight,
        updateIconColor: theme.colors.green,
      };
    }
    if (update.updateType === EngageCommunicationUpdateType.LetterDispatched) {
      return {
        updateIcon: ICONS.TruckArrowRightLight,
        updateIconColor: theme.colors.green,
      };
    }
    if (update.updateType === EngageCommunicationUpdateType.LetterDelivered) {
      return {
        updateIcon: ICONS.EnvelopeSentLight,
        updateIconColor: theme.colors.green,
      };
    }

    if (update.updateType === EngageCommunicationUpdateType.LetterReturned) {
      return {
        updateIcon: ICONS.TruckArrowLeftLight,
        updateIconColor: theme.colors.red,
      };
    }
    if (update.updateType === EngageCommunicationUpdateType.LetterPrinting) {
      return {
        updateIcon: ICONS.PrinterLight,
        updateIconColor: theme.colors.green,
      };
    }
    if (update.updateType === EngageCommunicationUpdateType.LetterCancelled) {
      return {
        updateIcon: ICONS.EnvelopeFailedLight,
        updateIconColor: theme.colors.red,
      };
    }

    if (USER_RESPONSE_UPDATES.includes(update.updateType)) {
      return {
        updateIcon: ICONS.CircleCheckLight,
        updateIconColor: theme.colors.green,
      };
    }
  }

  return {
    updateIcon: ICONS.LinkHorizontalLight,
    updateIconColor: theme.colors.green,
  };
};

const formatUpdateType = (
  updateType: EngageCommunicationUpdate['updateType'],
) => (updateType ? UPDATE_TYPE_LABELS[updateType] || updateType : 'Update');

const formatUpdateSource = (
  updateSource: EngageCommunicationUpdate['updateSource'],
) => (updateSource ? UPDATE_SOURCE_LABELS[updateSource] || updateSource : null);

const formatUpdateDate = (date: EngageCommunicationUpdate['statusDate']) =>
  formatDate(isoDateStringToDate(date), UI_DATE_FORMAT_LONG);

const CommunicationUpdateRow: React.FC<{
  update: EngageCommunicationUpdate;
}> = ({ update }) => {
  const { updateIcon, updateIconColor } = getUpdateIcon(update);
  return (
    <CardRow
      label={`${update.updateSource ? `${formatUpdateSource(update.updateSource)}` : ''} ${formatUpdateType(update.updateType)} ${formatUpdateDate(update.statusDate)} `}
      labelAlignTop
      labelNumberOfLines={2}
      icon={updateIcon}
      iconSize={23}
      iconColor={updateIconColor}
    ></CardRow>
  );
};

export const EngageCommunicationUpdatesModal: React.FC<
  ModalScreenProps<ModalScreens.EngageCommunicationUpdates>
> = ({ route }) => {
  const dispatch = useDispatch();
  const { communicationName, communicationUpdates } = route.params;
  const sortedCommunicationUpdates = React.useMemo(
    () => [...communicationUpdates].sort((a, b) => b.id - a.id),
    [communicationUpdates],
  );

  const handleClose = React.useCallback(() => {
    dispatch(NAVIGATE_MODAL_BACK([ModalScreens.EngageCommunicationUpdates]));
  }, [dispatch]);

  useEsc(handleClose);

  return (
    <Page
      scrollView
      header={
        <PageHeader
          style={{ marginBottom: -theme.paddingHorizontalHalf * 2 }}
          title="Message Updates"
          onPressCancelButton={handleClose}
        />
      }
    >
      <Card label={`${communicationName} Events:`}>
        {sortedCommunicationUpdates.length ? (
          sortedCommunicationUpdates.map((update) => (
            <CommunicationUpdateRow key={update.id} update={update} />
          ))
        ) : (
          <CardRow value="No Updates" />
        )}
      </Card>
    </Page>
  );
};
